import type { NarrativeValuatorCompany } from '@/pages/Narrative/hooks/narrativeValuatorCompanyFragment';
import { useNarrativeErrorToast } from '@/pages/Narrative/hooks/useNarrativeErrorToast';
import {
  hasErrors,
  useNarrativeForm,
} from '@/pages/Narrative/hooks/useNarrativeForm';
import { useNarrativeTracking } from '@/pages/Narrative/hooks/useNarrativeTracking';
import { type NarrativeForm } from '@/pages/Narrative/utils/schema';
import { useTracking } from 'react-tracking';
import { NarrativeMiniValuatorCompanySelector } from './NarrativeMiniValuatorCompanySelector';
import {
  narrativeMiniValuatorAppendModifiedFields,
  type NarrativeMiniValuatorScreen,
} from '../utils';
import { NarrativeMiniValuatorFairValueInput } from './NarrativeMiniValuatorFairValueInput/NarrativeMiniValuatorFairValueInput';
import { Button, cn } from '@simplywallst/ui-core/next';
import { TimeseriesPlot } from '../../TimeseriesPlot';
import { NarrativeMiniValuatorGrowth } from './NarrativeMiniValuatorGrowth';
import ResizeSolid from '@simplywallst/ui-core/icons/ResizeSolid';
import RefreshOutline from '@simplywallst/ui-core/icons/RefreshOutline';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import { currencyToSymbol } from '@/utilities/formatting';
import { NarrativeMiniValuatorPriceDiscount } from './NarrativeMiniValuatorPriceDiscount';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import GuestRegistrationModal from '@/components/GuestRegistrationModal';
import useUser from '@/hooks/useUser';
import { NarrativeMiniValuatorDiscardProgressConfirmation } from './NarrativeMiniValuatorDiscardProgressConfirmation';
import type { PastAnnualRevenueEarnings } from '@/pages/Narrative/types';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import type {
  FuturePEMultipleCalculationInput,
  FuturePEMultipleCalculationOutput,
  FuturePEMultipleFairValueCalculationInput,
  FuturePEMultipleFairValueCalculationOutput,
} from '../../Calculators/useFuturePEMultipleCalculation';
import { validateValuatorData } from '@/pages/Narrative/utils/validation';
import { NarrativePanelRegister } from '@/components/GuestRegistrationModal/components/NarrativePanelRegister';
import type { HeadingElements } from '@/utilities/heading';

type Props = {
  company: NarrativeValuatorCompany;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  setScreen: (screen: NarrativeMiniValuatorScreen) => void;
  initialValues: NarrativeForm;
  onSubmit: (values: NarrativeForm) => void;
  isSubmitting: boolean;
  fullScreenPath: string;
  saveLabel: ReactNode;
  enableReinitialize: boolean;
  calculateFairValue: ({
    discountRatePercentage,
    futurePE,
    profitMarginPercentage,
    revenueGrowthRatePercentage,
    timeFrame,
  }: FuturePEMultipleCalculationInput) => FuturePEMultipleCalculationOutput;
  calculateRevenueGrowthRateFromFairValue: ({
    discountRatePercentage,
    futurePE,
    profitMarginPercentage,
    timeFrame,
    fairValue,
  }: FuturePEMultipleFairValueCalculationInput) => FuturePEMultipleFairValueCalculationOutput;
  hideCompanySelect?: boolean;
  onSliderInteraction?: (isInteracting: boolean) => void;
  titleAs: HeadingElements;
};

export const NarrativeMiniValuatorForm = ({
  company,
  pastAnnualRevenueEarnings,
  initialValues,
  setScreen,
  onSubmit,
  isSubmitting,
  fullScreenPath,
  saveLabel,
  enableReinitialize,
  calculateFairValue,
  calculateRevenueGrowthRateFromFairValue,
  hideCompanySelect,
  onSliderInteraction,
  titleAs,
}: Props) => {
  const { info, name, tickerSymbol } = company;
  const { trackEvent } = useTracking();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const discardCallbackRef = useRef<() => void>();
  const {
    data: { loggedIn },
  } = useUser();

  const {
    values,
    errors,
    setPartialValues,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    hasErrors: hasFormErrors,
    resetForm,
    isDirty,
  } = useNarrativeForm({
    initialValues,
    validate: validateValuatorData,
    enableReinitialize,
  });
  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({ trackEvent, dirty, errors });

  const handleSubmit = () => {
    if (!loggedIn) return setShowRegistrationModal(true);

    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    onSubmit(values);

    setSubmitCount(0);
  };

  const handleOnFairValueChange =
    (values: NarrativeForm) => (fairValue: number) => {
      const { revenueGrowthRatePercentage } =
        calculateRevenueGrowthRateFromFairValue({
          fairValue,
          discountRatePercentage: values.discountRatePercentage,
          futurePE: values.priceEarningsMultiple,
          profitMarginPercentage: values.profitMarginPercentage,
          timeFrame: values.timePeriod,
        });

      setPartialValues({
        fairValue,
        revenuePercentage: revenueGrowthRatePercentage,
      });
    };

  const handleCalculationInputChange =
    (key: keyof NarrativeForm) => (value: number) => {
      const calculatorOutputs = calculateFairValue({
        discountRatePercentage: values.discountRatePercentage,
        futurePE: values.priceEarningsMultiple,
        profitMarginPercentage: values.profitMarginPercentage,
        revenueGrowthRatePercentage:
          key === 'revenuePercentage' ? value : values.revenuePercentage,
        timeFrame: key === 'timePeriod' ? value : values.timePeriod,
      });

      setPartialValues({
        ...values,
        ...calculatorOutputs,
      });
    };

  const tradingCurrencyIso =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? ('XXX' as const);
  const companyCurrencySymbol = currencyToSymbol[tradingCurrencyIso].currency
    .symbol as string;
  const lastSharePrice = company.analysisValue?.lastSharePrice ?? null;

  return (
    <NarrativeMiniValuatorDiscardProgressConfirmation
      isOpen={showDiscardConfirmation}
      onDiscard={() => {
        setShowDiscardConfirmation(false);
        if (!discardCallbackRef.current) return;

        discardCallbackRef.current();
        discardCallbackRef.current = undefined;
      }}
      onCancel={() => {
        setShowDiscardConfirmation(false);
        discardCallbackRef.current = undefined;
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        aria-invalid={hasFormErrors}
      >
        <div
          className={cn(
            'grid items-center bg-surface-3 px-x2 py-x1',
            hideCompanySelect && 'h-[68px]'
          )}
        >
          {!hideCompanySelect && (
            <NarrativeMiniValuatorCompanySelector
              onCompanySelect={() => {
                if (isDirty) {
                  discardCallbackRef.current = () => setScreen('companySelect');
                  return setShowDiscardConfirmation(true);
                }
                setScreen('companySelect');
              }}
              companyName={name}
              tickerSymbol={tickerSymbol}
              website={info?.url}
              titleAs={titleAs}
            />
          )}
          <div
            className={cn(
              'grid grid-cols-[130px_minmax(125px,auto)] gap-x1_5',
              !hideCompanySelect && 'mt-x1'
            )}
          >
            <NarrativeMiniValuatorFairValueInput
              currencySymbol={companyCurrencySymbol}
              onChange={handleOnFairValueChange(values)}
              value={values.fairValue}
            />

            <NarrativeMiniValuatorPriceDiscount
              currencyIso={tradingCurrencyIso}
              fairValue={values.fairValue}
              lastSharePrice={lastSharePrice}
              npvPerShare={company.analysisValue?.npvPerShare ?? null}
            />
          </div>
        </div>

        <div className="grid px-x2 pb-x2">
          <div className="mt-x1 grid h-[89px] items-center">
            <NarrativeMiniValuatorGrowth
              values={values}
              onRevenueChange={handleCalculationInputChange(
                'revenuePercentage'
              )}
              onTimePeriodChange={handleCalculationInputChange('timePeriod')}
              onSliderInteraction={onSliderInteraction}
            />
          </div>

          <div className="overflow-hidden">
            <TimeseriesPlot
              pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
              company={company}
              values={values}
              height={182.5}
            />
          </div>

          <div className="mt-x1 grid grid-cols-[1fr_repeat(3,auto)] justify-end gap-x1">
            <div>
              <Button
                href="https://www.youtube.com/watch?v=lbOjxgYSJ0A"
                rel="nofollow noopener"
                target="_blank"
                variant="subtle"
                size="medium"
                className="!px-x0_5"
                aria-label="Help"
                onClick={() => trackEvent({ action: 'help' })}
              >
                <InformationSolid />
              </Button>
            </div>
            <Button
              isDisabled={!isDirty}
              variant="secondary"
              size="medium"
              onClick={() => {
                trackEvent({ action: 'reset' });
                discardCallbackRef.current = resetForm;
                setShowDiscardConfirmation(true);
              }}
              className="!px-x0_5"
              aria-label="Reset form"
            >
              <RefreshOutline />
            </Button>
            <Button
              variant="secondary"
              href={narrativeMiniValuatorAppendModifiedFields({
                pathname: fullScreenPath,
                dirty,
                values,
              })}
              size="medium"
              onClick={() => trackEvent({ action: 'advanced' })}
              className="!p-x1"
            >
              <ResizeSolid />
              <span className="hidden @[350px]:inline">Advanced</span>
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              size="medium"
              onClick={() => trackEvent({ action: 'save' })}
            >
              {isSubmitting ? 'Saving' : saveLabel}
            </Button>
          </div>
        </div>
        {showRegistrationModal && (
          <GuestRegistrationModal
            canClose
            onClose={() => setShowRegistrationModal(false)}
            onLoginSuccess={() => setShowRegistrationModal(false)}
            onRegisterSuccess={() => setShowRegistrationModal(false)}
            removeWindowScroll
            SplashPanel={{
              Component: NarrativePanelRegister,
              isDark: true,
            }}
          />
        )}
      </form>
    </NarrativeMiniValuatorDiscardProgressConfirmation>
  );
};
